import Navigation from 'app/Navigation';
import RetailManager from 'app/retail_manager/RetailManager';
import BaseController from 'app/src/controllers/base/BaseController';
import CommonData from 'app/src/data/CommonData';
import CCTerminalCollection from 'app/src/data/models/CCTerminalCollection';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import GatewayTerminalListView from 'app/src/views/settings/paymentProcessing/GatewayTerminalListView';

export default class GatewayTerminalListViewController extends BaseController {
  /**
   *Constructs for a single Gateway view with the Terminals attached to this particular Gateway
   *
   * @param gatewayConfig {Model} A singular Gateway to build the view from
   * @param ccTerminals {Array} Array of Terminals that are attached to this particular Gateway
   */
  constructor(gatewayConfig, ccTerminals) {
    if (
      !(gatewayConfig instanceof GatewayConfig) ||
      !(ccTerminals instanceof CCTerminalCollection)
    ) {
      throw 'A Gateway and Terminal (even if empty) must be specified when constructing a GatewayTerminalListViewController';
    }

    super();

    this._gatewayConfig = gatewayConfig;
    this._ccTerminals = ccTerminals;

    const paymentProvider = new PaymentProvider(this._gatewayConfig, this._ccTerminals);
    const hasTerminalSupport = paymentProvider.activeGatewaySupportedTerminals !== null;

    this.view = new GatewayTerminalListView({
      model: this._gatewayConfig,
      viewData: {
        terminals: this._getTerminals(),
        hasTerminalSupport,
        iOSTerminalSupportOnly: paymentProvider.activeGatewayIosOnly,
        retailManagerActive: RetailManager.active && !RetailManager.isAndroid,
        isLSPayments: this._gatewayConfig.get('gatewayID') === 'lightspeed-payments',
        readOnly: window.merchantos?.session?.isReadOnly() || false,
      },
    });
  }

  /**
   * We cannot simply send the fetched Terminal collection to the viewData, as we need to
   * include a shop property of where the Terminal was last used.
   *
   * So we iterate through the Terminals, and match up the shopID with the name in the
   * fetched Shop collection.
   *
   * @private
   */
  _getTerminals() {
    let shops = CommonData.shops;
    let terminals = new CCTerminalCollection(
      this._ccTerminals.map((terminal) => {
        const shop = shops.findWhere({
          shopID: terminal.get('shopID'),
        });

        if (shop) {
          // expose the shop name as a property on the terminal model; we never save
          // this model (it's only for the view) so this is safe
          terminal.set('shopName', shop.get('name'));
        }

        return terminal;
      })
    );

    return terminals.toJSON();
  }

  /**
   * Event handler for when the add terminal link is clicked
   */
  onAddTerminalClicked() {
    Navigation.navigateToClientUrl(
      `settings/payment_processing/add_terminal/${this._gatewayConfig.id}`
    );
  }

  /**
   * Event handler for a terminal link is clicked
   *
   * @param terminalID {string|number}    The ccTerminalID of the terminal which was clicked
   */
  onEditTerminalClicked(terminalID) {
    Navigation.navigateToClientUrl(`settings/payment_processing/edit_terminal/${terminalID}`);
  }

  /**
   * Event handler for when Edit Gateway Link is clicked
   *
   * @param gatewayID {string|number}
   */
  onEditGatewayClicked(gatewayID) {
    Navigation.navigateToClientUrl(`settings/payment_processing/edit_gateway/${gatewayID}`);
  }
}
